// CreateCompanyFieldModal — serves BOTH create and edit ("Add company field" /
// "Edit company field"): Name + SQL column, a Data source file → Column cascade,
// and a Description. Create POSTs /company-field; passing `field` switches to
// edit, seeding from that row and PATCHing /company-field/:id.
//
// Everything except Description is required (the design only marks Name, but the
// contract needs all four), and SQL column additionally has to be a valid SQL
// identifier — see sqlColumnErrorKey. In EDIT mode `dbColumn` is disabled and
// left out of both the validation and the payload: a column name is fixed once
// the field exists. The cascade reads GET /datasource/selection (the same
// picker feed the form builder's dataSource binding drawer uses), which carries
// each file's column schema, so choosing a file needs no follow-up request.
// Only ids are persisted — the file's `_id` and the column's `columnId`.
//
// The draft re-seeds on every open, so Cancel really discards; required-field
// validation runs before the request (same `submitted` idiom as
// DataSourceModal).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import sqlIdentifierError, { SQL_IDENTIFIER_MAX } from '@/utils/sqlIdentifier';
import toast from 'react-hot-toast';
import { Button, Field, Icon, Input, Modal, SelectDropdown, Textarea } from '@/components/common';
import { useGetDataSourceSelectionQuery } from '@/api/endpoints/datasourceApi';
import {
  useCreateCompanyFieldMutation,
  useUpdateCompanyFieldMutation,
  type CompanyField,
} from '@/api/endpoints/companyFieldsApi';

interface CreateCompanyFieldModalProps {
  open: boolean;
  onClose: () => void;
  /** Present → edit that field instead of creating a new one. */
  field?: CompanyField | null;
}

// The shared lowercase-identifier rule (utils/sqlIdentifier), mapped to this
// module's own wording.
/** i18n key of the first rule the value breaks, or null when it's valid. */
const sqlColumnErrorKey = (value: string): string | null => {
  const rule = sqlIdentifierError(value);
  return rule ? `data_structure_cf_sql_column_${rule}` : null;
};

const emptyDraft = {
  fieldTitle: '',
  dbColumn: '',
  dataSource: '',
  dataSourceColumn: '',
  description: '',
};

const CreateCompanyFieldModal = ({ open, onClose, field }: CreateCompanyFieldModalProps) => {
  const { t } = useTranslation();
  const [createCompanyField, { isLoading: creating }] = useCreateCompanyFieldMutation();
  const [updateCompanyField, { isLoading: updating }] = useUpdateCompanyFieldMutation();
  const { data: sources = [] } = useGetDataSourceSelectionQuery(undefined, { skip: !open });
  const [draft, setDraft] = useState(emptyDraft);
  const [submitted, setSubmitted] = useState(false);

  const isEdit = Boolean(field);
  const saving = creating || updating;

  // Re-seed each time the modal opens — from the edited row, or blank for a new
  // field. Discards leftovers on close either way.
  useEffect(() => {
    if (!open) return;
    setDraft(
      field
        ? {
            fieldTitle: field.fieldTitle,
            dbColumn: field.dbColumn,
            dataSource: field.dataSourceId,
            dataSourceColumn: field.dataSourceColumnId,
            description: field.description,
          }
        : emptyDraft,
    );
    setSubmitted(false);
  }, [open, field]);

  const patch = (changes: Partial<typeof emptyDraft>) => setDraft((d) => ({ ...d, ...changes }));

  // Columns belong to the picked file, so switching files clears the choice.
  const pickSource = (dataSource: string) => patch({ dataSource, dataSourceColumn: '' });

  const columns = sources.find((s) => s.id === draft.dataSource)?.columns ?? [];

  // Everything but Description is required.
  const titleMissing = !draft.fieldTitle.trim();
  // Empty OR malformed — one field, three possible messages. Skipped in edit
  // mode: the input is disabled, so an existing value can't be corrected here.
  const dbColumnErrorKey = isEdit ? null : sqlColumnErrorKey(draft.dbColumn);
  const sourceMissing = !draft.dataSource;
  const columnMissing = !draft.dataSourceColumn;
  const invalid = titleMissing || Boolean(dbColumnErrorKey) || sourceMissing || columnMissing;

  const save = async () => {
    // Validate before any backend call: surface errors, bail while invalid.
    setSubmitted(true);
    if (invalid) return;
    const name = draft.fieldTitle.trim();
    try {
      if (field) {
        // No dbColumn — it can't change.
        await updateCompanyField({
          id: field.id,
          body: {
            fieldTitle: name,
            dataSource: draft.dataSource,
            dataSourceColumn: draft.dataSourceColumn,
            description: draft.description.trim(),
          },
        }).unwrap();
        toast.success(t('data_structure_cf_updated', { name }));
      } else {
        await createCompanyField({
          fieldTitle: name,
          dbColumn: draft.dbColumn.trim(),
          dataSource: draft.dataSource,
          dataSourceColumn: draft.dataSourceColumn,
          description: draft.description.trim(),
        }).unwrap();
        toast.success(t('data_structure_cf_created', { name }));
      }
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon='building-2'
      title={t(isEdit ? 'data_structure_cf_edit_title' : 'data_structure_cf_create_title')}
      subtitle={t(isEdit ? 'data_structure_cf_edit_subtitle' : 'data_structure_cf_create_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          <Button iconStart={isEdit ? 'check' : 'plus'} onClick={save} loading={saving}>
            {t(isEdit ? 'button_save' : 'data_structure_cf_create')}
          </Button>
        </>
      }
    >
      <div className='company-field-create'>
        <div className='company-field-create__row'>
          <Field
            label={t('data_structure_cf_name')}
            required
            error={submitted && titleMissing ? t('data_structure_cf_name_required') : undefined}
          >
            <Input
              value={draft.fieldTitle}
              placeholder={t('data_structure_cf_name_ph')}
              error={submitted && titleMissing}
              onChange={(e) => patch({ fieldTitle: e.target.value })}
            />
          </Field>
          <Field
            label={t('data_structure_cf_sql_column')}
            required={!isEdit}
            labelTag={
              isEdit ? (
                <>
                  <Icon name='lock' size={13} aria-hidden='true' />
                  {t('data_structure_cf_sql_column_locked')}
                </>
              ) : undefined
            }
            error={
              submitted && dbColumnErrorKey
                ? t(dbColumnErrorKey, { max: SQL_IDENTIFIER_MAX })
                : undefined
            }
          >
            <Input
              value={draft.dbColumn}
              placeholder={t('data_structure_cf_sql_column_ph')}
              maxLength={SQL_IDENTIFIER_MAX}
              disabled={isEdit}
              error={submitted && Boolean(dbColumnErrorKey)}
              onChange={(e) => patch({ dbColumn: e.target.value })}
            />
          </Field>
        </div>

        <div className='company-field-create__row'>
          <Field
            label={t('data_structure_cf_source')}
            required
            htmlFor='cf-source'
            error={submitted && sourceMissing ? t('data_structure_cf_source_required') : undefined}
          >
            <SelectDropdown
              id='cf-source'
              size='lg'
              value={draft.dataSource || null}
              placeholder={t('data_structure_cf_source_ph')}
              error={submitted && sourceMissing}
              options={sources.map((s) => ({ value: s.id, label: s.schemaName }))}
              onChange={(next) => pickSource(next)}
            />
          </Field>
          <Field
            label={t('data_structure_cf_column')}
            required
            htmlFor='cf-column'
            error={submitted && columnMissing ? t('data_structure_cf_column_required') : undefined}
          >
            <SelectDropdown
              id='cf-column'
              size='lg'
              value={draft.dataSourceColumn || null}
              placeholder={
                draft.dataSource
                  ? t('data_structure_cf_column_ph')
                  : t('data_structure_cf_column_ph_empty')
              }
              disabled={!draft.dataSource}
              error={submitted && columnMissing}
              options={columns.map((c) => ({ value: c.columnId, label: c.columnName }))}
              onChange={(next) => patch({ dataSourceColumn: next })}
            />
          </Field>
        </div>

        <Field label={t('data_structure_cf_description')} optional>
          <Textarea
            rows={4}
            value={draft.description}
            placeholder={t('data_structure_cf_description_ph')}
            onChange={(e) => patch({ description: e.target.value })}
          />
        </Field>
      </div>
    </Modal>
  );
};

export default CreateCompanyFieldModal;
